import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, ArrowUpRight, Zap } from "lucide-react";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { IMAGES } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

const REAL_WORK = [
  { src: "/api/files/speedyz/uploads/0a2dfa8a-0f08-4fe1-a223-71ee3bd327c1.webp", label: "Jordan backyard court — Sydney" },
  { src: "https://speedylinemarking.com.au/wp-content/uploads/2025/11/WhatsApp-Image-2025-11-03-at-12.31.55_3ed2de3c.jpg", label: "Sports court line marking" },
  { src: "/api/files/speedyz/uploads/8056e79e-6287-4cdc-a166-baa8b679b5fb.webp", label: "School multi-sport courts" },
];

const CrewCard = ({ f }) => (
  <div className="glass rounded-lg p-8 flex flex-col items-center text-center" data-testid={`crew-${f.id}`}>
    <div className="relative h-40 w-40 rounded-full overflow-hidden border border-gold/30 bg-obsidian-700 flex items-center justify-center">
      {f.image ? (
        <img {...responsive(f.image, "(min-width: 1024px) 400px, 100vw", [480, 800])} loading="lazy" decoding="async" alt={f.name} className="h-full w-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-2 text-zinc-600">
          <User size={48} strokeWidth={1} />
          <span className="font-mono text-[8px] uppercase tracking-widest px-3">Real team photo required</span>
        </div>
      )}
    </div>
    <h3 className="mt-5 font-heading font-black text-xl">{f.name}</h3>
    <p className="mt-1 text-gold text-sm font-medium">{f.role}</p>
    <p className="mt-3 text-sm text-zinc-400">{f.specialisation}</p>
  </div>
);

export default function About() {
  const [crew, setCrew] = useState([]);
  useEffect(() => { api.get("/founders").then((r) => setCrew(r.data)).catch(() => {}); }, []);

  return (
    <div className="pt-28 pb-24 min-h-screen noise" data-testid="about-page">
      <Seo title="About Team Speedy | Speedy Resurfacing — Sydney Court Resurfacing Crew"
           description="Meet Team Speedy — the crew behind Speedy Line Marking, now specialising in court resurfacing, restoration and custom court design across Sydney and NSW."
           path="/about" />
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <div className="text-center">
            <Overline className="justify-center">About</Overline>
            <h1 className="mt-5 font-heading font-black text-5xl md:text-7xl tracking-tighter">TEAM <span className="text-neon">SPEEDY.</span></h1>
            <p className="mt-4 font-mono text-xs md:text-sm uppercase tracking-[0.35em] gold-text">Speed Force — Resurfacing</p>
            <p className="mt-5 mx-auto max-w-xl text-zinc-400">
              The crew behind Sydney's trusted Speedy Line Marking — now bringing that same speed, precision and finish to full court resurfacing.
            </p>
          </div>
        </Reveal>

        {/* Real Speedy court work */}
        <Reveal delay={0.05}>
          <div className="mt-14 relative overflow-hidden rounded-lg border border-gold/20" data-testid="about-work-video">
            <img {...responsive(IMAGES.nightTennis, "(min-width: 1024px) 50vw, 100vw", [480, 800, 1200])} loading="lazy" decoding="async" alt="Court resurfacing by Team Speedy — fresh surface at golden hour"
                 className="h-[320px] md:h-[480px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-0 p-6 md:p-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon flex items-center gap-2">
                <Zap size={12} /> Real Speedy courts — on site
              </span>
            </div>
          </div>
        </Reveal>

        {/* Story */}
        <Reveal delay={0.1}>
          <div className="mt-20 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <Overline>The Speed Force</Overline>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-4xl tracking-tight">BUILT ON LINES.<br /><span className="gold-text">PERFECTED ON COURTS.</span></h2>
              <p className="mt-5 text-zinc-400">
                Team Speedy earned its name marking and resurfacing Sydney's sports courts — CM3 certified, trusted by schools and clubs, working round the clock.
              </p>
              <p className="mt-4 text-zinc-400">
                Speedy Resurfacing is that same force pointed at one mission: taking tired, cracked courts and rebuilding them from the surface up — colour, texture and millimetre-perfect lines.
              </p>
              <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 text-gold hover:text-gold-hover font-bold">
                See the transformations <ArrowUpRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {REAL_WORK.map((w, i) => (
                <figure key={w.src} className={`overflow-hidden rounded-lg border border-white/10 ${i === 0 ? "col-span-2" : ""}`}>
                  <img {...responsive(w.src, "(min-width: 1024px) 33vw, 50vw", [480, 800])} alt={w.label} loading="lazy" decoding="async" className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" style={{ maxHeight: i === 0 ? 280 : 200 }} />
                </figure>
              ))}
            </div>
          </div>
        </Reveal>

        {/* The crew */}
        {crew.length > 0 && (
          <Reveal delay={0.1}>
            <div className="mt-24">
              <div className="text-center">
                <Overline className="justify-center">The Crew</Overline>
                <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">ONE TEAM. <span className="text-neon">ONE STANDARD.</span></h2>
              </div>
              <div className="mt-12 grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {crew.map((f) => <CrewCard key={f.id} f={f} />)}
              </div>
              <p className="mt-6 text-center font-mono text-[11px] text-zinc-600">Crew details are editable in the admin dashboard until real team photos are uploaded.</p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <div className="mt-20 text-center">
            <Link to="/quote" data-testid="about-quote-cta"
                  className="inline-flex items-center gap-2 rounded-sm bg-neon px-8 py-4 font-bold text-obsidian shadow-neon hover:bg-neon-hover transition-colors">
              Work with Team Speedy <ArrowUpRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
